<script setup>
import { onMounted, ref } from 'vue';
import { useConfirm } from 'primevue';
import { ZIndex } from '@primeuix/utils/zindex';
import SlideGenerationSidebar from './Sidebar/SlideGenerationSidebar.vue';
import SnippetsSidebar from './Sidebar/SnippetsSidebar.vue';
import ImagesSidebar from './Sidebar/ImagesSidebar.vue';
import ChatSidebar from './Sidebar/ChatSidebar.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import generateImgSrc from '@/assets/images/plus-rectangle.svg';
import provenanceImgSrc from '@/assets/document.svg';
import editContentImgSrc from '@/assets/chat.svg';
import aiImageImgSrc from '@/assets/gallery.svg';

const confirm = useConfirm();

const props = defineProps(['presentationId'])

const visible = ref(false);
const drawer = ref();
const slideGenerationSidebar = ref(null);
const snippetsSidebar = ref(null);
const imagesSidebar = ref(null);
const chatSidebar = ref(null);
const slideGenerationStatus = ref("pending");  // pending - loading - finished - failed
const isSidebarOpen = ref(false);
const selectedSidebar = ref('');
const items = ref([
  {
    label: 'Generate',
    icon: generateImgSrc,
    command: () => {
      openDrawer();
      selectedSidebar.value = 'options-slide-generation-sidebar';
    }
  },
  {
    label: 'Provenance',
    icon: provenanceImgSrc,
    command: () => {
      openDrawer();
      selectedSidebar.value = 'options-snippets-sidebar';
    }
  },
  {
    label: 'Edit content',
    icon: editContentImgSrc,
    command: () => {
      openDrawer();
      selectedSidebar.value = 'options-chat-sidebar';
    }
  },
  {
    label: 'AI Images',
    icon: aiImageImgSrc,
    command: () => {
      openDrawer();
      selectedSidebar.value = 'options-images-sidebar';
    }
  }
]);

const sidebarsMap = {
  "options-slide-generation-sidebar": slideGenerationSidebar,
  "options-snippets-sidebar": snippetsSidebar,
  "options-images-sidebar": imagesSidebar,
  "options-chat-sidebar": chatSidebar,
};

onMounted(() => {
  drawer.value.onAfterLeave = function () {
    if (drawer.value.autoZIndex) {
      ZIndex.clear(drawer.value.mask);
    }

    drawer.value.unbindDocumentKeyDownListener();
    //drawer.value.containerVisible = false;
    drawer.value.disableDocumentSettings();
    this.$emit('after-hide');
  };
  drawer.value.hide = function () {
    drawer.value.onAfterLeave();
    let el = document.getElementsByClassName('p-drawer-mask')[0];
    el.childNodes[0].classList.add('p-drawer-leave-active');
    el.childNodes[0].classList.add('p-drawer-leave-to');

    setTimeout(function () {
      el.style.display = 'none';
    }, 400);
    return;
  };
});

function openDrawer() {
  if (visible.value) {
    setTimeout(function () {
      drawer.value.onEnter();
      drawer.value.onAfterEnter();

      let el = document.getElementsByClassName('p-drawer-mask')[0];
      el.style.display = 'flex';

      setTimeout(function () {
        el.childNodes[0].classList.remove('p-drawer-leave-active');
        el.childNodes[0].classList.remove('p-drawer-leave-to');
      }, 5);
    }, 5);
  } else {
    visible.value = true;
  }
}

function updateSlideGenerationLoader(value) {
  slideGenerationStatus.value = value;
  if (slideGenerationStatus.value === 'finished') {
    confirm1();
  }
}

const confirm1 = () => {
  confirm.require({
    group: 'positioned',
    message: 'Slides Generated!',
    header: 'Confirmation',
    icon: 'pi pi-exclamation-triangle',
    position: 'bottom',
    rejectProps: {
      label: 'Dismiss',
      severity: 'secondary',
      outlined: true
    },
    acceptProps: {
      label: 'Apply'
    },
    accept: () => {
      slideGenerationSidebar.value.apply();
    },
    reject: () => {
      slideGenerationSidebar.value.dismiss();
    }
  });
};

function toggleSidebar(id) {
  isSidebarOpen.value = true;
  for (const [_, sidebarComponent] of Object.entries(sidebarsMap)) {
    console.log(`PROBLEM ${_}`);
    if (sidebarComponent.value) {
      sidebarComponent.value.close();
    }
  }
  sidebarsMap[id].value.open();
}

</script>


<template>
  <Drawer ref="drawer" v-model:visible="visible" header="Drawer" position="full"
          @show="toggleSidebar(selectedSidebar)">
    <template #container="{ closeCallback }">
      <SlideGenerationSidebar ref="slideGenerationSidebar" :isSlideGenerationLoading="isSlideGenerationLoading"
                              :presentationId="presentationId" @onSidebarClosed="closeCallback"
                              @onSlideGenerationUpdate="updateSlideGenerationLoader" />
      <SnippetsSidebar ref="snippetsSidebar" :presentationId="presentationId" :isSidebarOpen="isSidebarOpen"
                       @onSidebarClosed="closeCallback" @onSidebarOpened="toggleSidebar('options-snippets-sidebar', $event)"
                       @image-clicked="imageClicked" />
      <ChatSidebar ref="chatSidebar" @onSidebarClosed="closeCallback" />
      <ImagesSidebar ref="imagesSidebar" @onSidebarClosed="closeCallback" />
    </template>
  </Drawer>
  <SpeedDial :model="items" direction="left" class="is-flex is-align-items-center"
                            style="position: absolute; top: calc(10% - 3rem); right: 1rem">
    <template #button="{ toggleCallback }">
      <Button rounded raised class="border" @click="toggleCallback"
              style="height:3rem;width:3rem;background-image: linear-gradient(45deg, #E100FF 0%, #7F00FF 100%); color: white; border: none;">
        <FontAwesomeIcon icon="ellipsis" />
      </Button>
    </template>
    <template #item="{ item, toggleCallback }">
      <a @click="toggleCallback"
                class="is-flex is-flex-direction-column is-flex-wrap-nowrap is-align-items-center p-1 m-1">
        <img :src="item.icon" style="width: 2rem;">
        <span class="has-text-weight-bold has-text-centered" style="font-size:0.7rem;color: #666666;">
          {{ item.label }}
        </span>
      </a>
    </template>
  </SpeedDial>
  <SlideGenerationSidebar ref="slideGenerationSidebar" v-show="false"
                          :isSlideGenerationLoading="isSlideGenerationLoading" :presentationId="presentationId"
                          @onSlideGenerationUpdate="updateSlideGenerationLoader" />
  <ConfirmDialog group="positioned">
    <template #container="{ message, acceptCallback, rejectCallback }">
      <div class="tooltiptext is-flex is-flex-direction-column m-2">
        <span>{{ message.message }}</span>
        <div class="is-flex">
          <button class="button m-2" @click="rejectCallback">Dismiss</button>
          <button class="button m-2"
                  style="background-image: linear-gradient(45deg, #E100FF 0%, #7F00FF 100%); color: white; border: none;"
                  @click="acceptCallback">Apply</button>
        </div>
      </div>
    </template>
  </ConfirmDialog>
</template>
